/* ==========================================================================
   Pinky — Shopify theme
   theme.js · Web components sin dependencias
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const routes = theme.routes;
  const strings = theme.strings;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  /* ------------------------------------------------------------------------
     Utilidades
     ------------------------------------------------------------------------ */
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const fetchSection = (url, sectionId) => {
    const glue = url.includes('?') ? '&' : '?';
    return fetch(`${url}${glue}section_id=${encodeURIComponent(sectionId)}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then((r) => {
      if (!r.ok) throw new Error(r.status);
      return r.text();
    });
  };

  const toast = (message) => {
    const el = document.getElementById('Toast');
    if (!el || !message) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el._timer);
    el._timer = setTimeout(() => el.classList.remove('is-visible'), 2600);
  };

  const setLoading = (button, loading) => {
    if (!button) return;
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (loading) button.setAttribute('disabled', '');
    else if (!button.dataset.keepDisabled) button.removeAttribute('disabled');
  };

  const updateCartCount = (count, totalHTML) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      if (el.matches('cart-drawer, cart-items')) return;
      el.textContent = count;
      el.hidden = Number(count) === 0;
    });
    if (totalHTML !== undefined) {
      document.querySelectorAll('small[data-cart-total]').forEach((el) => { el.innerHTML = totalHTML; });
    }
  };

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  /* ------------------------------------------------------------------------
     Drawers (menú, búsqueda, filtros, carrito)
     ------------------------------------------------------------------------ */
  const Drawers = {
    active: null,
    trigger: null,
    overlay: null,

    getOverlay() {
      if (!this.overlay) {
        this.overlay = document.createElement('div');
        this.overlay.className = 'drawer-overlay';
        this.overlay.addEventListener('click', () => this.close());
        document.body.appendChild(this.overlay);
      }
      return this.overlay;
    },

    open(id, trigger) {
      const drawer = document.getElementById(id);
      if (!drawer) return false;
      if (this.active && this.active !== drawer) this.close(false);
      this.active = drawer;
      this.trigger = trigger || document.activeElement;
      const overlay = this.getOverlay();
      // La capa va junto al drawer para compartir su contexto de apilamiento (p. ej. dentro del header fijo).
      if (overlay.nextElementSibling !== drawer) drawer.parentNode.insertBefore(overlay, drawer);
      drawer.classList.add('is-open');
      overlay.classList.add('is-visible');
      document.body.classList.add('is-locked');
      document.querySelectorAll(`[aria-controls="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      const autofocus = drawer.querySelector('[data-autofocus]');
      requestAnimationFrame(() => (autofocus || drawer).focus({ preventScroll: true }));
      return true;
    },

    close(restoreFocus = true) {
      const drawer = this.active;
      if (!drawer) return;
      drawer.classList.remove('is-open');
      this.getOverlay().classList.remove('is-visible');
      document.body.classList.remove('is-locked');
      document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this.active = null;
      if (restoreFocus && this.trigger && this.trigger.focus) this.trigger.focus({ preventScroll: true });
    }
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      Drawers.open(opener.dataset.drawerOpen, opener);
      return;
    }
    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      Drawers.close();
      return;
    }
    const cartOpener = event.target.closest('[data-cart-open]');
    if (cartOpener && document.getElementById('CartDrawer')) {
      event.preventDefault();
      Drawers.open('CartDrawer', cartOpener);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (Modals.active) Modals.close();
      else if (Drawers.active) Drawers.close();
    }
    if (event.key === 'Tab') {
      const container = Modals.active ? Modals.active.querySelector('.modal__dialog') : Drawers.active;
      if (!container) return;
      const items = Array.from(container.querySelectorAll(focusableSelector)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });

  /* ------------------------------------------------------------------------
     Modales (guía de tallas, vista rápida)
     ------------------------------------------------------------------------ */
  const Modals = {
    active: null,
    trigger: null,

    open(modal, trigger) {
      if (!modal) return;
      if (Drawers.active) Drawers.close(false);
      this.active = modal;
      this.trigger = trigger || document.activeElement;
      modal.hidden = false;
      document.body.classList.add('is-locked');
      const dialog = modal.querySelector('.modal__dialog');
      if (dialog) requestAnimationFrame(() => dialog.focus({ preventScroll: true }));
    },

    close() {
      const modal = this.active;
      if (!modal) return;
      modal.hidden = true;
      document.body.classList.remove('is-locked');
      this.active = null;
      if (this.trigger && this.trigger.focus) this.trigger.focus({ preventScroll: true });
    }
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-modal-open]');
    if (opener) {
      event.preventDefault();
      Modals.open(document.getElementById(opener.dataset.modalOpen), opener);
      return;
    }
    if (event.target.closest('[data-modal-close]')) {
      event.preventDefault();
      Modals.close();
    }
  });

  /* ------------------------------------------------------------------------
     Encabezado fijo
     ------------------------------------------------------------------------ */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky;
      this.section = this.closest('.section-header');
      if (!this.section || this.mode === 'none') return;
      this.section.classList.add('is-sticky');
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.section) this.section.classList.remove('is-sticky', 'is-hidden', 'is-scrolled');
    }

    onScroll() {
      const y = window.scrollY;
      const top = this.section.offsetTop;
      this.section.classList.toggle('is-scrolled', y > top + 4);
      if (this.mode === 'scroll-up') {
        const hide = y > this.lastY && y > top + this.offsetHeight + 80 && !Drawers.active;
        this.section.classList.toggle('is-hidden', hide);
      }
      this.lastY = y;
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ------------------------------------------------------------------------
     Barra de anuncios
     ------------------------------------------------------------------------ */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.announcement__message'));
      if (this.items.length < 2) return;
      this.index = 0;
      this.speed = Number(this.dataset.speed) || 5000;
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.start();
    }

    disconnectedCallback() { this.stop(); }

    start() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.stop();
      this.timer = setInterval(() => this.next(), this.speed);
    }

    stop() { clearInterval(this.timer); }

    next() {
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* ------------------------------------------------------------------------
     Carrusel del hero
     ------------------------------------------------------------------------ */
  class SlideshowComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
      this.fade = this.classList.contains('hero__main--fade');
      this.index = 0;
      if (this.slides.length < 2) return;

      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.sliderDot), true)));
      const prev = this.querySelector('[data-slider-prev]');
      const next = this.querySelector('[data-slider-next]');
      if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1, true));
      if (next) next.addEventListener('click', () => this.goTo(this.index + 1, true));

      this.autoplay = this.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.speed = Number(this.dataset.speed) || 6000;
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.play());
      this.bindSwipe();
      this.play();

      if (designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          const i = this.slides.indexOf(event.target);
          if (i > -1) { this.pause(); this.goTo(i); this.locked = true; }
        });
        document.addEventListener('shopify:block:deselect', () => { this.locked = false; this.play(); });
      }
    }

    disconnectedCallback() { this.pause(); }

    bindSwipe() {
      let startX = null;
      this.addEventListener('pointerdown', (e) => { startX = e.clientX; }, { passive: true });
      this.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 50) this.goTo(this.index + (dx < 0 ? 1 : -1), true);
        startX = null;
      });
    }

    play() {
      if (!this.autoplay || this.locked) return;
      this.pause();
      this.timer = setInterval(() => this.goTo(this.index + 1), this.speed);
    }

    pause() { clearInterval(this.timer); }

    goTo(i, user = false) {
      const total = this.slides.length;
      this.index = (i + total) % total;
      if (this.fade) {
        this.slides.forEach((s, n) => s.classList.toggle('is-active', n === this.index));
      } else {
        this.track.style.transform = `translateX(-${this.index * 100}%)`;
        this.slides.forEach((s, n) => s.classList.toggle('is-active', n === this.index));
      }
      this.slides.forEach((s, n) => {
        if (n === this.index) s.removeAttribute('aria-hidden');
        else s.setAttribute('aria-hidden', 'true');
      });
      this.dots.forEach((d, n) => {
        d.classList.toggle('is-active', n === this.index);
        if (n === this.index) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
      if (user) this.play();
    }
  }
  customElements.define('slideshow-component', SlideshowComponent);

  /* ------------------------------------------------------------------------
     Pestañas
     ------------------------------------------------------------------------ */
  class TabsComponent extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!this.tabs.length) return;
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); this.select((i + 1) % this.tabs.length, true); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.select((i - 1 + this.tabs.length) % this.tabs.length, true); }
        });
      });
      if (designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          const i = this.tabs.indexOf(event.target);
          if (i > -1) this.select(i);
        });
      }
    }

    select(index, focus = false) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !active;
        if (active && focus) tab.focus();
      });
    }
  }
  customElements.define('tabs-component', TabsComponent);

  /* ------------------------------------------------------------------------
     Búsqueda predictiva
     ------------------------------------------------------------------------ */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results || this.dataset.enabled !== 'true') return;
      this.input.addEventListener('input', debounce(() => this.onInput(), 280));
      this.input.addEventListener('focus', () => { if (this.results.innerHTML.trim() && this.input.value.trim()) this.toggle(true); });
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.toggle(false); });
    }

    onInput() {
      const term = this.input.value.trim();
      if (term.length < 2) { this.toggle(false); return; }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: term,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,page,query',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each'
      });
      fetch(`${routes.predictiveSearch}?${params.toString()}`, { signal: this.controller.signal })
        .then((r) => r.text())
        .then((html) => {
          const content = parseHTML(html).querySelector('.predictive');
          this.results.innerHTML = content ? content.outerHTML : '';
          this.toggle(Boolean(content));
        })
        .catch(() => {});
    }

    toggle(open) {
      this.results.hidden = !open;
      this.input.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    onKeydown(e) {
      if (this.results.hidden) return;
      const options = Array.from(this.results.querySelectorAll('.predictive__term, .predictive__product, .predictive__all'));
      if (!options.length) return;
      const current = options.findIndex((o) => o.getAttribute('aria-selected') === 'true');
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const next = e.key === 'ArrowDown' ? (current + 1) % options.length : (current - 1 + options.length) % options.length;
        options.forEach((o, i) => o.setAttribute('aria-selected', i === next ? 'true' : 'false'));
        options[next].scrollIntoView({ block: 'nearest' });
      }
      if (e.key === 'Enter' && current > -1) {
        e.preventDefault();
        options[current].click();
      }
      if (e.key === 'Escape') this.toggle(false);
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------------
     Selector de cantidad
     ------------------------------------------------------------------------ */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', () => this.step(button.name === 'plus' ? 1 : -1));
      });
      this.input.addEventListener('change', () => this.validate());
      this.updateButtons();
    }

    step(direction) {
      const step = Number(this.input.step) || 1;
      const min = Number(this.input.min) || 0;
      const max = this.input.max ? Number(this.input.max) : Infinity;
      let value = (Number(this.input.value) || 0) + direction * step;
      if (direction < 0 && value < min) value = min === 0 ? 0 : min;
      if (value > max) value = max;
      if (value === Number(this.input.value)) return;
      this.input.value = value;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    validate() {
      const min = Number(this.input.min) || 0;
      const max = this.input.max ? Number(this.input.max) : Infinity;
      let value = Number(this.input.value);
      if (Number.isNaN(value) || value < min) value = min;
      if (value > max) value = max;
      if (String(value) !== this.input.value) this.input.value = value;
      this.updateButtons();
    }

    updateButtons() {
      const value = Number(this.input.value);
      const min = Number(this.input.min) || 0;
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const minus = this.querySelector('[name="minus"]');
      const plus = this.querySelector('[name="plus"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = value >= max;
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------------------------
     Selector de variantes
     ------------------------------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants]');
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.sectionId = this.dataset.section;
      this.form = document.getElementById(this.dataset.form);
      this.root = this.closest('[data-product-section]') || document;
      this.minDefault = Number(this.dataset.minDefault) || 0;
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('.variant-picker__option')).map((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        const select = fieldset.querySelector('select');
        return checked ? checked.value : select ? select.value : null;
      });
    }

    onChange() {
      const selected = this.selectedOptions();
      this.querySelectorAll('.variant-picker__option').forEach((fieldset, i) => {
        const label = fieldset.querySelector('[data-selected-value]');
        if (label) label.textContent = selected[i];
      });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      this.updateAvailability();
      this.updateButton(variant);
      if (!variant) return;
      this.updateInputs(variant);
      this.updatePrice(variant);
      this.updateQuantity(variant);
      this.updateMedia(variant);
      if (this.hasAttribute('data-update-url')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        this.renderDynamic(variant);
      }
    }

    updateAvailability() {
      const selected = this.selectedOptions();
      this.querySelectorAll('.variant-picker__option').forEach((fieldset, index) => {
        fieldset.querySelectorAll('input[data-option-input]').forEach((input) => {
          const available = this.variants.some((v) => v.available && v.options[index] === input.value &&
            v.options.every((o, i) => i === index || o === selected[i]));
          input.classList.toggle('is-unavailable', !available);
        });
      });
    }

    updateInputs(variant) {
      if (!this.form) return;
      const idInput = this.form.querySelector('[data-variant-id]') || this.form.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updateButton(variant) {
      if (!this.form) return;
      const button = this.form.querySelector('[data-add-button]');
      if (!button) return;
      const label = button.querySelector('.btn__label');
      if (!variant) {
        button.setAttribute('disabled', '');
        if (label) label.textContent = strings.unavailable;
      } else if (!variant.available) {
        button.setAttribute('disabled', '');
        if (label) label.textContent = strings.soldOut;
      } else {
        button.removeAttribute('disabled');
        if (label) label.textContent = strings.addToCart;
      }
    }

    updatePrice(variant) {
      const price = this.root.querySelector(`#Price-${this.sectionId}`);
      if (!price) return;
      const current = price.querySelector('[data-price-current]');
      const compare = price.querySelector('[data-price-compare]');
      const wrapper = price.querySelector('.price');
      if (current) current.innerHTML = variant.price;
      if (compare) {
        compare.innerHTML = variant.compare || '';
        compare.hidden = !variant.compare;
      }
      if (wrapper) wrapper.classList.toggle('price--on-sale', Boolean(variant.compare));
      const pvp = this.root.querySelector(`#Pvp-${this.sectionId}`);
      if (pvp) pvp.textContent = variant.pvp || '';
      const sku = this.root.querySelector(`#Sku-${this.sectionId}`);
      if (sku) {
        const span = sku.querySelector('span');
        if (span) span.textContent = variant.sku || '';
        sku.hidden = !variant.sku;
      }
    }

    updateQuantity(variant) {
      const input = document.querySelector(`[data-product-quantity][form="${this.dataset.form}"]`);
      const min = Math.max(variant.min || 1, this.minDefault || 0);
      if (input) {
        input.min = variant.min || 1;
        input.step = variant.step || 1;
        if (variant.max) input.max = variant.max; else input.removeAttribute('max');
        if (Number(input.value) < min) input.value = min;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const rules = this.root.querySelector(`#QuantityRules-${this.sectionId}`);
      if (rules) {
        let text = '';
        if (min > 1 || variant.step > 1) {
          text = strings.ruleMin.replace('[min]', min);
          if (variant.step > 1) text += `, ${strings.ruleStep.replace('[step]', variant.step)}`;
        }
        rules.textContent = text;
      }
    }

    updateMedia(variant) {
      if (!variant.media) return;
      const gallery = this.root.querySelector('media-gallery');
      if (gallery && gallery.setActive) gallery.setActive(String(variant.media), true);
    }

    renderDynamic(variant) {
      const root = this.root;
      const url = root.dataset && root.dataset.productUrl;
      if (!url) return;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      fetch(`${url}?variant=${variant.id}&section_id=${this.sectionId}`, { signal: this.controller.signal })
        .then((r) => r.text())
        .then((html) => {
          const doc = parseHTML(html);
          root.querySelectorAll('[data-dynamic]').forEach((el) => {
            const fresh = doc.getElementById(el.id);
            if (fresh) {
              el.innerHTML = fresh.innerHTML;
              el.className = fresh.className;
              el.hidden = fresh.hidden;
            }
          });
        })
        .catch(() => {});
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------------
     Galería de producto
     ------------------------------------------------------------------------ */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewer = this.querySelector('[data-gallery-viewer]');
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.counter = this.querySelector('[data-gallery-current]');
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.setActive(thumb.dataset.thumb)));
      if (this.viewer && this.counter) {
        this.viewer.addEventListener('scroll', debounce(() => this.updateCounter(), 60), { passive: true });
      }
    }

    items() { return Array.from(this.viewer.querySelectorAll('.gallery__item')); }

    setActive(mediaId, fromVariant = false) {
      const item = this.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!item) return;
      this.items().forEach((el) => el.classList.toggle('is-active', el === item));
      this.thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.thumb === String(mediaId)));
      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      if (!isDesktop) {
        this.viewer.scrollTo({ left: item.offsetLeft - this.viewer.offsetLeft - 16, behavior: 'smooth' });
      } else if (fromVariant && !this.classList.contains('gallery--slider')) {
        this.viewer.prepend(item);
        const top = this.getBoundingClientRect().top + window.scrollY - 140;
        if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
      }
    }

    updateCounter() {
      const items = this.items();
      const left = this.viewer.scrollLeft;
      let index = 0;
      items.forEach((el, i) => { if (el.offsetLeft - this.viewer.offsetLeft - 20 <= left) index = i; });
      this.counter.textContent = index + 1;
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ------------------------------------------------------------------------
     Agregar al carrito
     ------------------------------------------------------------------------ */
  const renderCartDrawer = (html) => {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer || !html) return;
    const fresh = parseHTML(html).getElementById('CartDrawer');
    if (!fresh) return;
    drawer.innerHTML = fresh.innerHTML;
    drawer.dataset.cartCount = fresh.dataset.cartCount;
    drawer.dataset.cartTotal = fresh.dataset.cartTotal;
    updateCartCount(fresh.dataset.cartCount, fresh.dataset.cartTotal);
  };

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.error = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    onSubmit(event) {
      const drawer = document.getElementById('CartDrawer');
      if (theme.cartType !== 'drawer' || !drawer) return;
      event.preventDefault();
      const button = event.submitter || this.form.querySelector('[type="submit"]');
      if (button && button.hasAttribute('aria-busy') && button.getAttribute('aria-busy') === 'true') return;
      setLoading(button, true);
      if (this.error) this.error.hidden = true;

      const body = new FormData(this.form);
      body.append('sections', 'cart-drawer');
      body.append('sections_url', window.location.pathname);

      fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.status) {
            const message = data.description || data.message || strings.cartError;
            if (this.error) { this.error.textContent = message; this.error.hidden = false; }
            else toast(message);
            return;
          }
          if (data.sections) renderCartDrawer(data.sections['cart-drawer']);
          if (Modals.active) Modals.close();
          Drawers.open('CartDrawer', button);
        })
        .catch(() => toast(strings.cartError))
        .finally(() => setLoading(button, false));
    }
  }
  customElements.define('product-form', ProductForm);

  /* ------------------------------------------------------------------------
     Carrito: lógica compartida para el lateral y la página
     ------------------------------------------------------------------------ */
  const changeLine = (line, quantity, sections) => fetch(`${routes.cartChange}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ line, quantity, sections, sections_url: window.location.pathname })
  }).then((r) => r.json());

  const bindCartLines = (host, onUpdate) => {
    const run = (line, quantity) => {
      const item = host.querySelector(`[data-line-item="${line}"]`);
      if (item) item.classList.add('is-loading');
      onUpdate(line, quantity);
    };
    const debounced = debounce(run, 450);
    host.addEventListener('change', (e) => {
      const input = e.target.closest('[data-line-quantity]');
      if (input) debounced(Number(input.dataset.line), Number(input.value));
    });
    host.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-line-remove]');
      if (remove) {
        e.preventDefault();
        run(Number(remove.dataset.lineRemove), 0);
      }
    });
  };

  const showCartError = (host, message) => {
    const error = host.querySelector('[data-cart-error]');
    if (error) { error.textContent = message || strings.cartError; error.hidden = false; }
    else toast(message || strings.cartError);
  };

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      bindCartLines(this, (line, quantity) => {
        changeLine(line, quantity, ['cart-drawer'])
          .then((data) => {
            if (data.status) {
              showCartError(this, data.description || data.message);
              this.querySelectorAll('.line-item.is-loading').forEach((el) => el.classList.remove('is-loading'));
              return;
            }
            renderCartDrawer(data.sections && data.sections['cart-drawer']);
          })
          .catch(() => showCartError(this));
      });
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      bindCartLines(this, (line, quantity) => {
        changeLine(line, quantity, [this.sectionId])
          .then((data) => {
            if (data.status) {
              showCartError(this, data.description || data.message);
              this.querySelectorAll('.line-item.is-loading').forEach((el) => el.classList.remove('is-loading'));
              return;
            }
            const html = data.sections && data.sections[this.sectionId];
            const fresh = html && parseHTML(html).querySelector('cart-items');
            if (fresh) {
              this.innerHTML = fresh.innerHTML;
              updateCartCount(fresh.dataset.cartCount, fresh.dataset.cartTotal);
            }
          })
          .catch(() => showCartError(this));
      });

      this.addEventListener('change', debounce((e) => {
        if (!e.target.matches('[data-cart-note]')) return;
        fetch(`${routes.cartUpdate}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: e.target.value })
        });
      }, 300));
    }
  }
  customElements.define('cart-items', CartItems);

  /* ------------------------------------------------------------------------
     Filtros de colección y búsqueda (AJAX)
     ------------------------------------------------------------------------ */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.form = this.querySelector('form');
      if (!this.form) return;
      const debounced = debounce(() => this.submit(), 450);
      this.form.addEventListener('change', (e) => {
        if (e.target.matches('[data-price-input]')) return;
        debounced();
      });
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-remove], [data-facets-clear]');
        if (link) { e.preventDefault(); this.render(link.href); }
      });
      document.addEventListener('click', (e) => {
        this.querySelectorAll('.facets--horizontal .facet[open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
      });
      this.addEventListener('toggle', (e) => {
        if (!e.target.matches('.facet') || !e.target.open) return;
        if (!this.classList.contains('facets--horizontal') || !window.matchMedia('(min-width: 990px)').matches) return;
        this.querySelectorAll('.facet[open]').forEach((d) => { if (d !== e.target) d.removeAttribute('open'); });
      }, true);
    }

    submit() {
      const params = new URLSearchParams();
      new FormData(this.form).forEach((value, key) => { if (value !== '') params.append(key, value); });
      const action = this.form.getAttribute('action');
      const query = params.toString();
      this.render(query ? `${action}?${query}` : action);
    }

    render(url) {
      const results = document.querySelector(`#ProductGrid-${this.sectionId}`);
      if (results) results.classList.add('is-loading');
      fetchSection(url, this.sectionId)
        .then((html) => {
          const doc = parseHTML(html);
          const freshResults = doc.querySelector(`#ProductGrid-${this.sectionId}`);
          if (results && freshResults) results.innerHTML = freshResults.innerHTML;

          ['ActiveFacets', 'ProductCount'].forEach((prefix) => {
            const current = document.getElementById(`${prefix}-${this.sectionId}`);
            const fresh = doc.getElementById(`${prefix}-${this.sectionId}`);
            if (current && fresh) current.innerHTML = fresh.innerHTML;
          });

          this.querySelectorAll('.facet__panel, .facet__summary').forEach((el) => {
            const fresh = doc.getElementById(el.id);
            if (fresh) el.innerHTML = fresh.innerHTML;
          });

          const openButton = this.querySelector('.facets__open');
          const freshOpen = doc.querySelector('.facets__open');
          if (openButton && freshOpen) openButton.innerHTML = freshOpen.innerHTML;

          const cleanUrl = new URL(url, window.location.origin);
          cleanUrl.searchParams.delete('section_id');
          window.history.replaceState({}, '', cleanUrl.pathname + cleanUrl.search);
        })
        .catch(() => { window.location.href = url; })
        .finally(() => { if (results) results.classList.remove('is-loading'); });
    }
  }
  customElements.define('facet-filters', FacetFilters);

  class LoadMore extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('[data-load-more]');
      if (button) button.addEventListener('click', () => this.load(button));
    }

    load(button) {
      const gridId = this.dataset.grid;
      const sectionId = gridId.replace('ProductGrid-', '');
      setLoading(button, true);
      fetchSection(this.dataset.next, sectionId)
        .then((html) => {
          const doc = parseHTML(html);
          const grid = document.querySelector(`#${gridId} [data-product-grid]`);
          const freshGrid = doc.querySelector(`#${gridId} [data-product-grid]`);
          if (grid && freshGrid) Array.from(freshGrid.children).forEach((child) => grid.appendChild(child));
          const freshMore = doc.querySelector(`#${gridId} load-more`);
          if (freshMore) this.replaceWith(freshMore);
          else this.remove();
        })
        .catch(() => { window.location.href = this.dataset.next; });
    }
  }
  customElements.define('load-more', LoadMore);

  /* ------------------------------------------------------------------------
     Vista rápida
     ------------------------------------------------------------------------ */
  class QuickViewModal extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-modal-content]');
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-quick-view]');
        if (!trigger) return;
        e.preventDefault();
        this.show(trigger.dataset.quickView, trigger);
      });
    }

    show(url, trigger) {
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      Modals.open(this, trigger);
      fetchSection(url, 'quick-view')
        .then((html) => {
          const fresh = parseHTML(html).querySelector('.quick-view');
          if (!fresh) throw new Error('empty');
          this.content.innerHTML = fresh.outerHTML;
          if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
        })
        .catch(() => { window.location.href = url; })
        .finally(() => this.classList.remove('is-loading'));
    }
  }
  customElements.define('quick-view-modal', QuickViewModal);

  /* ------------------------------------------------------------------------
     Favoritos
     ------------------------------------------------------------------------ */
  const Wishlist = {
    key: 'pinky:wishlist',

    get() {
      try { return JSON.parse(localStorage.getItem(this.key)) || []; } catch (e) { return []; }
    },

    set(list) {
      try { localStorage.setItem(this.key, JSON.stringify(list)); } catch (e) { /* almacenamiento no disponible */ }
      this.sync();
      document.dispatchEvent(new CustomEvent('wishlist:change', { detail: list }));
    },

    toggle(handle) {
      const list = this.get();
      const index = list.indexOf(handle);
      if (index > -1) list.splice(index, 1);
      else list.unshift(handle);
      this.set(list);
      return index === -1;
    },

    sync() {
      const list = this.get();
      document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
        const active = list.includes(button.dataset.handle);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        button.setAttribute('aria-label', active ? strings.wishlistRemove : strings.wishlistAdd);
      });
      document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
        el.textContent = list.length;
        el.hidden = list.length === 0;
      });
      document.querySelectorAll('[data-wishlist-label]').forEach((el) => {
        const template = list.length === 1 ? el.dataset.one : el.dataset.other;
        if (template) el.textContent = template.replace('[count]', list.length);
      });
    }
  };

  if (theme.wishlistEnabled) {
    document.addEventListener('click', (e) => {
      const button = e.target.closest('[data-wishlist-toggle]');
      if (!button) return;
      e.preventDefault();
      const added = Wishlist.toggle(button.dataset.handle);
      button.classList.add('is-bump');
      setTimeout(() => button.classList.remove('is-bump'), 250);
      toast(added ? strings.wishlistAdd : strings.wishlistRemove);
    });
  }

  class WishlistGrid extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.render();
      document.addEventListener('wishlist:change', () => this.prune());
    }

    render() {
      const list = Wishlist.get();
      if (!list.length) { this.empty.hidden = false; return; }
      Promise.all(list.map((handle) => fetchSection(`${routes.root.replace(/\/$/, '')}/products/${handle}`, 'wishlist-item')
        .then((html) => parseHTML(html).querySelector('[data-wishlist-item]'))
        .catch(() => null)))
        .then((items) => {
          items.filter(Boolean).forEach((item) => this.grid.appendChild(item));
          this.empty.hidden = this.grid.children.length > 0;
          Wishlist.sync();
        });
    }

    prune() {
      const list = Wishlist.get();
      this.grid.querySelectorAll('[data-wishlist-item]').forEach((item) => {
        if (!list.includes(item.dataset.wishlistItem)) item.remove();
      });
      this.empty.hidden = this.grid.children.length > 0;
    }
  }
  customElements.define('wishlist-grid', WishlistGrid);

  /* ------------------------------------------------------------------------
     Compartir
     ------------------------------------------------------------------------ */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button');
      const status = this.querySelector('.share__status');
      if (!button) return;
      button.addEventListener('click', () => {
        const data = { title: this.dataset.title, url: this.dataset.url };
        if (navigator.share) {
          navigator.share(data).catch(() => {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(data.url).then(() => {
            if (status) status.textContent = strings.linkCopied;
            setTimeout(() => { if (status) status.textContent = ''; }, 2500);
          });
        }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  /* ------------------------------------------------------------------------
     Productos recomendados
     ------------------------------------------------------------------------ */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('.product-grid')) return;
      const load = () => {
        fetch(this.dataset.url)
          .then((r) => r.text())
          .then((html) => {
            const fresh = parseHTML(html).querySelector('product-recommendations');
            if (fresh && fresh.innerHTML.trim()) {
              this.innerHTML = fresh.innerHTML;
              Wishlist.sync();
            } else {
              const section = this.closest('.shopify-section');
              if (section) section.hidden = true;
            }
          })
          .catch(() => {});
      };
      if (!('IntersectionObserver' in window)) { load(); return; }
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) { observer.disconnect(); load(); }
      }, { rootMargin: '0px 0px 400px 0px' });
      observer.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------------------------
     Formularios de país / idioma
     ------------------------------------------------------------------------ */
  document.addEventListener('change', (e) => {
    if (e.target.matches('select[data-autosubmit]')) e.target.form.submit();
  });

  /* ------------------------------------------------------------------------
     Animación al hacer scroll
     ------------------------------------------------------------------------ */
  const initReveal = () => {
    if (!document.body.classList.contains('has-reveal') || designMode || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const targets = document.querySelectorAll('.main-content .shopify-section > .section .page-width');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -60px 0px' });
    targets.forEach((el) => {
      if (el.getBoundingClientRect().top > window.innerHeight) {
        el.classList.add('reveal');
        observer.observe(el);
      }
    });
  };

  /* ------------------------------------------------------------------------
     Inicio
     ------------------------------------------------------------------------ */
  const init = () => {
    Wishlist.sync();
    initReveal();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', () => Wishlist.sync());
})();
